import type { FrameColor, Product } from "@/models/product";
import type { WindowFabricColor } from "@/store/selection";
import { findColorByHex } from "./findColorByHex";
import { findFrameByOuterHex } from "./findFrameByOuterHex";

/**
 * Resolves the default fabric + frame selection for a freshly-opened window.
 *
 * Fabric and frame share the same three-step priority:
 *
 * **Frame** (sun protection only):
 *   1. Frame whose hex matches the kozijn's outer colour.
 *   2. `previousFrameColorId` — a frame the user already picked on another
 *      window in this session.
 *   3. First frame in the product palette.
 *
 * **Fabric**:
 *   1. *(sun protection only)* Fabric whose hex matches the chosen frame's
 *      hex (so the configured product visually lines up with the kozijn).
 *      Skipped for window decoration since there is no frame.
 *   2. `previousFabricColorId` — a fabric the user already picked on
 *      another window in this session.
 *   3. First TRANSLUCENT fabric in the palette; if none, the first fabric
 *      regardless of type.
 *
 * Window decoration has no frame, so the frame steps AND fabric step 1
 * are skipped. Returns `null` when there are no fabric colours to pick from.
 */
export function resolveWindowDefaults(
    product: Product,
    outerHex: string | undefined,
    previousFabricColorId: string | null,
    previousFrameColorId: string | null,
): Omit<WindowFabricColor, "windowId"> | null {
    // Skip the frame steps entirely for window decoration — there is no
    // frame, so neither outer-hex matching nor previousFrameColorId apply.
    let frame: FrameColor | null = null;
    if (product.hasFrameColor) {
        const matchedFrame = outerHex
            ? findFrameByOuterHex(product, outerHex)
            : null;
        const previousFrame = previousFrameColorId
            ? (product.frameColors.find((f) => f.id === previousFrameColorId) ??
              null)
            : null;
        frame = matchedFrame ?? previousFrame ?? product.frameColors[0] ?? null;
    }

    // Skip the frame-hex lookup for window decoration: there is no frame to
    // match against, so previousFabricColorId becomes priority 1 directly.
    const fabricByFrameHex = product.hasFrameColor
        ? findColorByHex(product.fabricColors, frame?.hexColor)
        : null;
    const previousFabric = previousFabricColorId
        ? (product.fabricColors.find((c) => c.id === previousFabricColorId) ??
          null)
        : null;
    // Priority 3: prefer TRANSLUCENT — lichtdoorlatend is the everyday
    // default; SUN_BLOCKING is the niche choice.
    const firstTranslucent =
        product.fabricColors.find((c) => c.type === "TRANSLUCENT") ?? null;
    const fabric =
        fabricByFrameHex ??
        previousFabric ??
        firstTranslucent ??
        product.fabricColors[0] ??
        null;
    if (!fabric?.id) return null;

    return {
        fabricType: fabric.type ?? "TRANSLUCENT",
        colorId: fabric.id,
        colorHex: fabric.hexColor ?? null,
        colorName: fabric.name ?? null,
        pricePerCm2: fabric.price ?? null,
        frameColorId: frame?.id ?? null,
        frameColorName: frame?.name ?? null,
        frameColorHex: frame?.hexColor ?? null,
        // Sun-protection windows default to REMOTE (handzender);
        // window-decoration windows have no control-type concept.
        controlType: product.productType === "SUN_PROTECTION" ? "REMOTE" : null,
    };
}
